import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik } from "formik";
import { addIncorporateFilingSchema } from "formValidations/addIncorporateFilingSchema";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import React from "react";
import ReactCountryFlag from "react-country-flag";
import { Product, useGetAllProductsQuery } from "store/apis/products";
import { ModalProps } from "types/utils/modal";
import { entitiesSortedByCountry } from "utils/entitiesSortedByCountry";

const usHoldCoIncorporationProductId =
  process.env.PUBLIC_US_HOLD_CO_INCORPORATION_PRODUCT_ID;
const usSubsidiaryIncorporationProductId =
  process.env.PUBLIC_US_SUBSIDIARY_INCORPORATION_PRODUCT_ID;

const aboutDescriptions = [
  "Inkle Incorporate assists founders in setting up a US Delaware C-Corp in a compliant manner.",
  "For Indian resident founders based globally.",
  "US Holding company or subsidiary options possible.",
];

export const AddIncorporateFiling = ({
  isOpen,
  close,
  handleSubmit,
  openCalendly,
}: ModalProps & {
  handleSubmit: (val: { entity: string; productId: string }) => void;
  openCalendly: () => void;
}) => {
  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityId = entities[0]?.uuid; // only to get products
  const { data: products, isSuccess: isProductsSuccess } =
    useGetAllProductsQuery(
      {
        groupId,
        entityId,
        product_type: "TAX",
      },
      { skip: !groupId || !entityId }
    );
  const taxProducts = Object.values(products?.TAX || {}).flat();
  const { usEntities } = entitiesSortedByCountry(entities);

  const entityOptions = usEntities.map((entity) => ({
    label: (
      <div className="t-flex t-items-center t-gap-2 group-[[data-disabled]]:t-text-neutral-30">
        <ReactCountryFlag
          countryCode={entity.country_code}
          svg
          title={entity.country}
        />
        <span className="t-truncate t-max-w-36">{entity.name}</span>
      </div>
    ),
    value: entity.uuid,
  }));

  const productsData = taxProducts.filter(
    (product) =>
      (product as Product).uuid === usHoldCoIncorporationProductId ||
      (product as Product).uuid === usSubsidiaryIncorporationProductId
  );

  const productOptions = productsData.map((product) => ({
    label: (product as Product).title,
    value: product.uuid,
  }));

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <Modal.Title>Assisted Incorporation</Modal.Title>
          <Modal.Close onClick={close} />
        </Modal.Header>
        <Formik
          validateOnChange
          validationSchema={addIncorporateFilingSchema}
          onSubmit={handleSubmit}
          initialValues={{
            entity: "",
            productId: "",
          }}
        >
          {({ isSubmitting, values }) => {
            const selectedProduct = productsData.find(
              (product) => product.uuid === values.productId
            );
            return (
              <Form className="t-w-full t-m-0">
                <Modal.Body>
                  <div className="t-flex t-gap-4">
                    <div className="t-flex-1">
                      <Combobox
                        withForm
                        label="Select Entity"
                        placeholder="Select"
                        options={entityOptions}
                        name="entity"
                        block
                        menuPortalTarget={document.body}
                        components={{
                          ClearIndicator: () => null,
                        }}
                        isLoading={!entities?.length}
                      />
                    </div>
                    <div className="t-flex-1">
                      <Combobox
                        withForm
                        label="Select Structure"
                        placeholder="Select"
                        options={productOptions}
                        name="productId"
                        block
                        menuPortalTarget={document.body}
                        components={{
                          ClearIndicator: () => null,
                        }}
                        isLoading={!isProductsSuccess}
                      />
                      <div
                        role="button"
                        className="t-mt-1.5 t-text-body-sm t-text-purple"
                        onClick={openCalendly}
                      >
                        Unsure of your structure? Book a call
                      </div>
                    </div>
                  </div>
                  <hr className="t-text-neutral-10" />
                  <div>
                    <div className="t-text-body-sm t-text-text-30">About</div>
                    <ul className="t-space-y-1 t-mt-2">
                      {aboutDescriptions.map((desc, index) => (
                        <li
                          className="t-text-body-sm t-text-text-100"
                          key={index}
                        >
                          {desc}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="t-text-body-sm t-text-text-30">Pricing</div>
                    <div className="t-text-subtitle-lg t-text-text-100 t-mt-1">
                      $ {(selectedProduct as Product)?.approx_price || "999"}
                    </div>
                  </div>
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button type="button">Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    type="submit"
                    customType="primary"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    Next
                  </Button>
                </Modal.FooterButtonGroup>
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
